/**
 * @description API service for time clock kiosk operations
 *
 * Key features:
 * - Company config and employee list (public)
 * - PIN authentication
 * - Clock punch operations
 * - Status, history, and schedule queries
 * - PIN change
 *
 * @dependencies
 * - apiClient: Configured axios instance
 */

import apiClient from './apiClient';

const clockService = {
  // ========== Public endpoints (no auth) ==========

  /**
   * Get company config (name, timezone) for display
   */
  getConfig: async (companyId) => {
    const response = await apiClient.get(`/timeclock/${companyId}/config`);
    return response.data;
  },

  /**
   * Get active employee list for selection screen
   */
  getEmployees: async (companyId) => {
    const response = await apiClient.get(`/timeclock/${companyId}/employees`);
    return response.data.employees;
  },

  /**
   * Authenticate with PIN → returns session token
   */
  authenticate: async (companyId, employeeId, pin) => {
    const response = await apiClient.post(`/timeclock/${companyId}/auth`, {
      employeeId,
      pin,
    });
    return response.data;
  },

  // ========== Authenticated endpoints (session token) ==========

  /**
   * Perform a punch action (clock_in, clock_out, break_start, break_end)
   */
  punch: async (companyId, action) => {
    const response = await apiClient.post(`/timeclock/${companyId}/punch`, { action });
    return response.data;
  },

  /**
   * Get current clock status
   */
  getStatus: async (companyId) => {
    const response = await apiClient.get(`/timeclock/${companyId}/status`);
    return response.data;
  },

  /**
   * Get recent clock history
   */
  getHistory: async (companyId, limit = 14) => {
    const response = await apiClient.get(`/timeclock/${companyId}/history`, {
      params: { limit },
    });
    return response.data.entries;
  },

  /**
   * Get upcoming schedule (shifts)
   */
  getSchedule: async (companyId) => {
    const response = await apiClient.get(`/timeclock/${companyId}/schedule`);
    return response.data.shifts;
  },

  /**
   * Change own PIN (requires current PIN)
   */
  changePin: async (companyId, currentPin, newPin) => {
    const response = await apiClient.put(`/timeclock/${companyId}/change-pin`, {
      currentPin,
      newPin,
    });
    return response.data;
  },

  // ========== PTO endpoints (session token) ==========

  /**
   * Get employee's PTO balances
   */
  getPtoBalances: async (companyId) => {
    const response = await apiClient.get(`/timeclock/${companyId}/pto/balances`);
    return response.data.balances;
  },

  /**
   * Get employee's PTO requests
   */
  getPtoRequests: async (companyId) => {
    const response = await apiClient.get(`/timeclock/${companyId}/pto/requests`);
    return response.data.requests;
  },

  /**
   * Submit a new PTO request
   */
  submitPtoRequest: async (companyId, requestData) => {
    const response = await apiClient.post(`/timeclock/${companyId}/pto/requests`, requestData);
    return response.data.request;
  },

  /**
   * Cancel a pending PTO request
   */
  cancelPtoRequest: async (companyId, requestId, reason) => {
    const response = await apiClient.delete(`/timeclock/${companyId}/pto/requests/${requestId}`, {
      data: { reason },
    });
    return response.data;
  },
};

export default clockService;
